import { useCallback } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { Member } from '../../types/domain';
import { userApiClient } from '../../infrastructure/userApi/client';

type UpdateMemberRequest = Parameters<typeof userApiClient.updateMember>[1];

type UseMembersData = {
  isLoading: boolean;
  members: Member[];
  updateMember: (userId: string, changes: UpdateMemberRequest) => Promise<void>;
  removeMember: (userId: string) => Promise<void>;
};

const EMPTY_DATA = [] as const satisfies Member[];

const useMembersData = (): UseMembersData => {
  const queryClient = useQueryClient();

  const { data, isFetching } = useQuery({
    queryKey: ['members'],
    queryFn: async (): Promise<Member[]> => {
      return userApiClient.getOrganizationMembers();
    },
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    meta: { dataType: 'others' },
  });

  const { mutateAsync: updateMutation } = useMutation({
    mutationFn: ({
      userId,
      changes,
    }: {
      userId: string;
      changes: UpdateMemberRequest;
    }): Promise<void> => {
      return userApiClient.updateMember(userId, changes);
    },
    onSuccess: (): Promise<void> => {
      return queryClient.invalidateQueries({ queryKey: ['members'] });
    },
  });

  const { mutateAsync: removeMutation } = useMutation({
    mutationFn: (userId: string): Promise<void> => {
      return userApiClient.removeMember(userId);
    },
    onSuccess: (): Promise<void> => {
      return queryClient.invalidateQueries({ queryKey: ['members'] });
    },
  });

  const updateMember = useCallback(
    (userId: string, changes: UpdateMemberRequest): Promise<void> => {
      return updateMutation({ userId, changes });
    },
    [updateMutation]
  );

  return {
    isLoading: isFetching,
    members: data ?? EMPTY_DATA,
    updateMember: updateMember,
    removeMember: removeMutation,
  };
};

export { useMembersData };
